/**
 * ProductAutomationTab V2
 *
 * Tab für Zeitpläne und temporäre Versionen.
 * Zeigt aktive, geplante und vergangene Schedules.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, CalendarCheck, Info, X, ChevronDown, ChevronUp, User } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getSchedules } from '@/lib/api/publishing-schedules';
import { formatScheduleDate, formatRelativeTime } from '@/hooks/usePublishSchedule';
import { useCancelSchedule } from '@/hooks/useActiveSchedule';
import type { PublishingSchedule } from '@/types/publishing';
import { Button, Badge, Alert, AlertDescription, Card, CardContent, Loading, EmptyState } from '@/components/v2/ui';

interface ProductAutomationTabProps {
  productId: string;
}

export function ProductAutomationTab({ productId }: ProductAutomationTabProps) {
  const { t } = useTranslation();
  const [isPastSchedulesExpanded, setIsPastSchedulesExpanded] = useState(false);

  // Query schedules for this product
  const schedulesQuery = useQuery({
    queryKey: ['schedules', 'product', productId],
    queryFn: async () => {
      const result = await getSchedules({});
      return result.schedules;
    },
    staleTime: 30_000,
  });

  // Group schedules by status
  const allSchedules = schedulesQuery.data || [];
  const activeSchedules = allSchedules.filter((s) => s.status === 'active');
  const upcomingSchedules = allSchedules.filter((s) => s.status === 'scheduled');
  const pastSchedules = allSchedules.filter(
    (s) => s.status === 'completed' || s.status === 'canceled'
  );

  // Loading State
  if (schedulesQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading size="lg" label={t('products.loadingSchedules')} />
      </div>
    );
  }

  return (
    <div className="product-automation-tab space-y-6">
      {/* Active Schedule Banner - Prominent */}
      {activeSchedules.length > 0 && (
        <div className="space-y-3">
          {activeSchedules.map((schedule) => (
            <ActiveScheduleBanner key={schedule.id} schedule={schedule} />
          ))}
        </div>
      )}

      {/* Info Text */}
      <Alert variant="info">
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong className="font-semibold">{t('products.manageSchedules')}</strong>
          <p className="mt-1 text-sm">
            Neue Zeitpläne werden über den Versionen-Verlauf erstellt. Öffne dazu den "Versionen"
            Button bei einem Content Block oder Channel Package.
          </p>
        </AlertDescription>
      </Alert>

      {/* Upcoming Schedules */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <h6 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 flex items-center gap-2">
            <Clock className="h-5 w-5" />
            {t('products.plannedSchedules')}
          </h6>
          {upcomingSchedules.length > 0 && (
            <Badge variant="info">{upcomingSchedules.length}</Badge>
          )}
        </div>

        {upcomingSchedules.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <CalendarCheck className="h-12 w-12 text-neutral-400 mx-auto mb-3" />
              <p className="text-neutral-600 dark:text-neutral-400">{t('content.noPlannedSchedules')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {upcomingSchedules.map((schedule) => (
              <ScheduleCard key={schedule.id} schedule={schedule} variant="scheduled" />
            ))}
          </div>
        )}
      </div>

      {/* Past Schedules - Collapsible */}
      {pastSchedules.length > 0 && (
        <div>
          <button
            onClick={() => setIsPastSchedulesExpanded(!isPastSchedulesExpanded)}
            className="w-full flex items-center justify-between p-4 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800 dark:bg-neutral-800 transition-colors"
          >
            <span className="font-semibold text-neutral-900 dark:text-neutral-50 flex items-center gap-2">
              <Clock className="h-5 w-5" />
              {t('products.pastSchedules')} ({pastSchedules.length})
            </span>
            {isPastSchedulesExpanded ? (
              <ChevronUp className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
            )}
          </button>

          {isPastSchedulesExpanded && (
            <div className="mt-2 space-y-0 border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
              {pastSchedules.slice(0, 20).map((schedule, idx) => (
                <div
                  key={schedule.id}
                  className={idx < pastSchedules.length - 1 && idx < 19 ? 'border-b border-neutral-200 dark:border-neutral-800' : ''}
                >
                  <ScheduleCard schedule={schedule} variant="completed" />
                </div>
              ))}
              {pastSchedules.length > 20 && (
                <div className="text-center py-3 text-neutral-600 dark:text-neutral-400 text-sm bg-neutral-50 dark:bg-neutral-800">
                  + {pastSchedules.length - 20} weitere
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Complete Empty State */}
      {allSchedules.length === 0 && (
        <EmptyState
          icon={<CalendarCheck className="h-12 w-12" />}
          title={t('products.noSchedules')}
          description={t('content.createScheduleHint')}
        />
      )}
    </div>
  );
}

/**
 * Active Schedule Banner - Prominent Warning
 */
interface ActiveScheduleBannerProps {
  schedule: PublishingSchedule;
}

function ActiveScheduleBanner({ schedule }: ActiveScheduleBannerProps) {
  const { t } = useTranslation();
  const cancelMutation = useCancelSchedule(schedule.entity_type, schedule.entity_id);

  const handleCancel = () => {
    if (
      confirm(
        t('products.cancelActiveSchedule')
      )
    ) {
      cancelMutation.mutate(schedule.id);
    }
  };

  return (
    <Alert variant="warning">
      <Clock className="h-5 w-5" />
      <div className="flex-1">
        <AlertDescription>
          <strong className="font-semibold">{t('products.temporaryVersionActive')}</strong>
          <div className="text-sm mt-1">
            {schedule.entity_type === 'content_block' ? 'Content Block' : 'Channel Package'}
            {schedule.version && ` – V${schedule.version.version_number}`}
            <span className="text-neutral-600 dark:text-neutral-400 ml-2">
              bis {formatScheduleDate(schedule.publish_end_at)}
            </span>
          </div>
        </AlertDescription>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={handleCancel}
        disabled={cancelMutation.isPending}
        className="ml-4 shrink-0"
      >
        {cancelMutation.isPending ? (
          <Loading size="sm" />
        ) : (
          <>
            <X className="h-4 w-4 mr-2" />
            {t('common.cancel')}
</>
        )}
      </Button>
    </Alert>
  );
}

/**
 * Schedule Card
 */
interface ScheduleCardProps {
  schedule: PublishingSchedule;
  variant: 'scheduled' | 'completed';
}

function ScheduleCard({ schedule, variant }: ScheduleCardProps) {
  const { t } = useTranslation();
  const cancelMutation = useCancelSchedule(schedule.entity_type, schedule.entity_id);

  const handleCancel = () => {
    if (confirm('Zeitplan abbrechen?')) {
      cancelMutation.mutate(schedule.id);
    }
  };

  return (
    <Card
      className={
        variant === 'scheduled'
          ? 'border-primary-200'
          : 'border-0 rounded-none shadow-none'
      }
    >
      <CardContent className="py-3">
        <div className="flex justify-between items-start gap-4">
          {/* Left: Info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Badge variant={variant === 'scheduled' ? 'info' : 'secondary'}>
                {schedule.entity_type === 'content_block'
                  ? 'Content Block'
                  : schedule.entity_type === 'channel_package'
                    ? 'Channel Package'
                    : schedule.entity_type}
              </Badge>
              {schedule.version && (
                <Badge variant="secondary">V{schedule.version.version_number}</Badge>
              )}
              {schedule.status === 'canceled' && <Badge variant="error">Abgebrochen</Badge>}
            </div>
            {schedule.version?.diff_summary && (
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">
                "{schedule.version.diff_summary}"
              </p>
            )}
            {/* Meta */}
            <div className="text-sm text-neutral-600 dark:text-neutral-400 flex items-center gap-2">
              <User className="h-3 w-3" />
              {schedule.created_by_name || 'System'}
              <span>•</span>
              {formatRelativeTime(schedule.created_at)}
            </div>
          </div>

          {/* Right: Dates + Actions */}
          <div className="text-right flex-shrink-0">
            <div className="text-sm text-neutral-900 dark:text-neutral-50 mb-2">
              {formatScheduleDate(schedule.publish_start_at)}
              <span className="text-neutral-600 dark:text-neutral-400 mx-1">→</span>
              {formatScheduleDate(schedule.publish_end_at)}
            </div>
            {variant === 'scheduled' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? (
                  <Loading size="sm" />
                ) : (
                  <>
                    <X className="h-4 w-4 mr-1" />
                    {t('common.cancel')}
        </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
